import { Link } from "react-router-dom";
import { Users, Crown, ArrowUpRight, Save, UserPlus } from "lucide-react";

/**
 * CircleOfTheWeekCard — Dashboard weekly re-engagement card.
 *
 * Renders nothing when the user has no circles (backend returns
 * circles_count === 0).
 */
export default function CircleOfTheWeekCard({ data }) {
  if (!data || !data.circles_count) return null;

  const stats = [
    {
      key: "circles",
      icon: <Users size={14} />,
      label: `${data.circles_count} circle${data.circles_count !== 1 ? "s" : ""}`,
    },
    {
      key: "saves",
      icon: <Save size={14} />,
      label: `${data.combined_saves_this_week} rescue${data.combined_saves_this_week !== 1 ? "s" : ""} this week`,
    },
    {
      key: "joiners",
      icon: <UserPlus size={14} />,
      label: `${data.new_joiners_this_week} new joiner${data.new_joiners_this_week !== 1 ? "s" : ""}`,
    },
  ];

  return (
    <div
      className="card-flat mb-6 relative overflow-hidden animate-fadeup"
      data-testid="dashboard-circle-week-card"
      style={{
        background:
          "linear-gradient(135deg, var(--warm-grad-1) 0%, var(--warm-grad-2) 55%, var(--warm-grad-3) 100%)",
        border: "1px solid rgba(45,90,64,0.14)",
      }}
    >
      {/* subtle radial accent */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-10 h-48 w-48 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(224,158,80,0.22) 0%, rgba(224,158,80,0) 70%)",
        }}
      />

      <div className="relative p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(45,90,64,0.12)", color: "#2D5A40" }}
            >
              <Crown size={18} strokeWidth={2.2} />
            </div>
            <div>
              <div className="label-eyebrow" style={{ color: "#576A5E" }}>
                Circle of the Week
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-0.5">
                {formatRange(data.week_start, data.week_end)}
              </div>
            </div>
          </div>
          <Link
            to="/circles"
            className="btn-ghost text-sm"
            data-testid="circle-week-view-btn"
          >
            View circles <ArrowUpRight size={14} />
          </Link>
        </div>

        {data.hook && (
          <h3
            className="text-lg md:text-xl font-medium leading-snug tracking-tight mt-4"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="circle-week-hook"
          >
            {data.hook}
          </h3>
        )}

        {data.spotlight && !data.spotlight.is_self && (
          <SpotlightRow spotlight={data.spotlight} />
        )}

        {/* Stats strip */}
        <div className="mt-4 flex items-center gap-4 flex-wrap text-xs text-[var(--text-secondary)]">
          {stats.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span style={{ color: "#437A55" }}>{s.icon}</span>
              {s.label}
            </span>
          ))}
        </div>

        {/* Fun stat pill */}
        {data.fun_stat && (
          <div
            className="mt-4 inline-flex items-baseline gap-2 px-3 py-2 rounded-md"
            style={{ background: "rgba(45,90,64,0.07)" }}
            data-testid="circle-week-fun-stat"
          >
            <span
              className="num-md"
              style={{ color: "#2D5A40" }}
            >
              {data.fun_stat.value}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
              {data.fun_stat.label}
            </span>
            <span className="text-xs text-[var(--text-muted)] ml-2">
              {data.fun_stat.detail}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SpotlightRow({ spotlight }) {
  const initial = (spotlight.name || "?").trim().charAt(0).toUpperCase();
  return (
    <Link
      to={`/circles/${spotlight.circle_id}`}
      className="mt-3 inline-flex items-center gap-3 group"
      data-testid="circle-week-spotlight"
    >
      {spotlight.picture ? (
        <img
          src={spotlight.picture}
          alt=""
          className="h-8 w-8 rounded-full object-cover border border-[var(--border-default)]"
        />
      ) : (
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold"
          style={{
            background: "rgba(224,158,80,0.18)",
            color: "#A16A25",
          }}
        >
          {initial}
        </div>
      )}
      <div className="text-sm">
        <span className="font-medium">{spotlight.name}</span>
        <span className="text-[var(--text-muted)]"> · </span>
        <span className="font-mono">{spotlight.current_streak}mo</span>
        <span className="text-[var(--text-muted)]"> in </span>
        <span
          className="text-[var(--accent-primary)] group-hover:underline"
          style={{ textUnderlineOffset: 3 }}
        >
          {spotlight.circle_name}
        </span>
      </div>
    </Link>
  );
}

function formatRange(start, end) {
  try {
    const s = new Date(start);
    const e = new Date(end);
    const fmt = (d) =>
      d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${fmt(s)} – ${fmt(e)}`;
  } catch {
    return "";
  }
}
